import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import dayjs from 'dayjs';
import { firstValueFrom, startWith, takeUntil } from 'rxjs';

import { SDate } from '@summit/core';
import { DisplayType, SummitIdObject } from '@summit/models';

import { LayoutService } from '@summit/ui';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { v4 as uuid } from 'uuid';
import { SummitControl } from '../../models';
import { SFormErrorInputComponent } from '../s-form-error-input';
import { SFormInputDateComponent } from '../s-form-input-date/s-form-input-date.component';
import { SFormInputTimeComponent } from '../s-form-input-time/s-form-input-time.component';
import { SFormRowComponent } from '../s-form-row/s-form-row.component';

@Component({
  selector: 's-form-input-date-time',
  templateUrl: './form-input-date-time.component.html',
  standalone: true,
  imports: [
    CommonModule,
    DatePicker,
    FloatLabel,
    SFormErrorInputComponent,
    FormsModule,
    SFormInputDateComponent,
    SFormInputTimeComponent,
    SFormRowComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputDateTimeComponent<T extends SummitIdObject>
  extends SummitControl<T>
  implements OnInit, AfterViewInit
{
  @Input() showSeconds: boolean;
  @Input() shiftTZ: boolean;
  @Input() splitInputs: boolean;
  @Output()
  DateChange: EventEmitter<dayjs.Dayjs | null> = new EventEmitter<dayjs.Dayjs | null>();
  inputId: string;
  dateLabel?: string;
  timeLabel?: string;
  convertUtcToLocal: boolean;
  dateValue?: Date;

  constructor(protected layoutService: LayoutService) {
    super();
  }

  override ngOnInit() {
    this.inputId = (this.fieldName ?? uuid()) + this.groupName;
    super.ngOnInit();
    const attribute = this.schema ? this.schema?.refColumnList[this.fieldName ?? ''] : undefined;
    this.convertUtcToLocal =
      attribute?.displayType === DisplayType.Time ||
      attribute?.displayType === DisplayType.DateTimeLocal ||
      attribute?.displayType === DisplayType.DateTimeSecLocal;

    //console.log(`SFormInputDateTimeComponent: convertUtcToLocal: ${this.convertUtcToLocal}`, attribute);
    this.displayHint ??= this.attribute?.displayHint;
    this.dateLabel = this.label;
    if (this.dateLabel?.includes('Time')) {
      this.dateLabel = this.dateLabel.replace('Time', 'Date');
    }
    this.timeLabel = this.label;
    if (this.timeLabel?.includes('Date')) {
      this.timeLabel = this.timeLabel.replace('Date', 'Time');
    }
  }

  ngAfterViewInit() {
    const fieldName = this.fieldName?.toString();
    if (!fieldName) {
      console.error(`SFormInputDateComponent: Missing fieldName`);
      return;
    }
    //Conversion from Reactive Forms to PrimeNG Calendar
    if (!this.form.controls[this.fieldName ?? '']) {
      console.error(`SFormInputDateComponent: No form control for ${this.fieldName ?? ''}`);
      return;
    }
    this.form.controls[this.fieldName ?? ''].valueChanges
      .pipe(startWith(this.form.controls[this.fieldName ?? ''].value), takeUntil(this.disposing$))
      .subscribe((value) => {
        //console.log('Value change: ', value, this.shiftTZ, this.convertUtcToLocal);
        const lclDate = this.convertUtcToLocal
          ? value
            ? SDate.tz(SDate.newUtc(value), dayjs.tz.guess())
            : null
          : value
            ? SDate.ShiftToTz(SDate.newUtc(value), dayjs.tz.guess())
            : null;
        //console.log(`${this.fieldName.toString()}: Value change: `, lclDate, value.form());
        if (!lclDate) {
          this.dateValue = undefined;
        } else if (!this.dateValue || !dayjs(lclDate).isSame(dayjs(this.dateValue))) {
          //console.log(`${this.fieldName.toString()}: Control change: `, lclDate, this.dateValue);
          this.dateValue = lclDate.toDate(); //using value instead of lclDate to avoid conversion
        }
        this.cd.detectChanges();
      });
  }

  private dialogOpen = false;
  async onShow() {
    this.dialogOpen = true;
    const isMobile = await firstValueFrom(this.layoutService.mobile$);
    if (isMobile) {
      document.getElementById(this.inputId)?.blur();
    }
  }
  async onClose() {
    this.dialogOpen = false;
    this.onChange(); //since change is triggered first
  }

  onChange() {
    const fieldName = this.fieldName?.toString();
    if (!fieldName || !this.form.controls[fieldName]) return;
    if (this.dialogOpen) return;
    console.log(`${this.fieldName}: onChange`, this.form.controls[this.fieldName ?? ''].value, this.dateValue);
    if (this.dateValue === null || this.dateValue === undefined || this.dateValue.toString() === '') {
      this.form.controls[this.fieldName ?? ''].setValue(null);
      this.DateChange.emit(undefined);
    } else {
      console.log(`${this.fieldName}: onChange setting value to`, this.dateValue);
      const updatedDate = SDate.new(this.dateValue);
      console.log(`${this.fieldName}: onChange setting value to #2`, updatedDate.format());
      console.log('convertUtcToLocal', this.convertUtcToLocal);
      const lclDate = this.convertUtcToLocal ? SDate.tz(updatedDate, 'utc') : SDate.ShiftToTz(updatedDate, 'utc');
      console.log(`${this.fieldName}: onChange setting value to #3`, lclDate.format());
      this.form.controls[this.fieldName ?? ''].setValue(lclDate);
      this.DateChange.emit(updatedDate);
    }
    this.form.controls[this.fieldName ?? ''].markAsTouched();
    this.form.controls[this.fieldName ?? ''].markAsDirty();
    //}
  }

  get value(): Date {
    return this.form.controls[this.fieldName ?? ''].value; //.clone().startOf('day').toDate();
  }

  getObject(): dayjs.Dayjs {
    return this.value ? SDate.newUtc(this.value) : SDate.newUtc();
  }
}
